import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import classroomExamService from '../../../services/classroomExamService';
import documentService from '../../../services/documentService';
import estudiantesService from '../../../services/estudiantesService';
import types from '../../../services/types';
import utils from '../../../utils/generalFunctions';
import Loading from '../../../components/Loading';
import HtmlEditor from '../../../components/HtmlEditor';
import sanitizeHtml from '../../../utils/sanitizeHtml';
import '../AulaVirtual.css';

const OBJECTIVE_TYPES = [types.classroomQuestionTypes.VERDADERO_FALSO, types.classroomQuestionTypes.OPCION_UNICA, types.classroomQuestionTypes.OPCION_MULTIPLE];

const ExamManage = () => {
    const { examId } = useParams();
    const [exam, setExam] = useState(null);
    const [loading, setLoading] = useState(true);
    const esDocente = Number(utils.getUserInformation()?.userTypeId) === types.userTypes.TEACHER;

    const load = async () => {
        try {
            const e = await classroomExamService.getExam(examId);
            setExam(e);
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    };

    useEffect(() => { load(); }, [examId]);

    if (loading) return <Loading />;
    if (!exam || !exam.examId) return <div className="alert alert-warning av-module">No encontramos este examen.</div>;

    return (
        <div className="container-fluid p-0 av-module">
            {esDocente ? <ExamTeacherView exam={exam} onSaved={load} /> : <ExamStudentView exam={exam} />}
        </div>
    );
};

// =====================================================================
// Vista docente: configuración, preguntas/ponderación, roster
// =====================================================================
const ExamTeacherView = ({ exam, onSaved }) => {
    const [editando, setEditando] = useState(false);
    const [formData, setFormData] = useState(exam);
    const [examQuestions, setExamQuestions] = useState([]);
    const [weightStatus, setWeightStatus] = useState(null);
    const [banco, setBanco] = useState([]);
    const [preguntaSeleccionada, setPreguntaSeleccionada] = useState('');
    const [pesoNuevo, setPesoNuevo] = useState('');
    const [loadingPreguntas, setLoadingPreguntas] = useState(true);

    const cargarPreguntas = async () => {
        setLoadingPreguntas(true);
        try {
            const eq = await classroomExamService.getExamQuestions(exam.examId, false);
            setExamQuestions(eq || []);
            const ws = await classroomExamService.getWeightStatus(exam.examId);
            setWeightStatus(ws);
            const todas = await classroomExamService.getQuestions(exam.classroomId);
            const yaUsadas = new Set((eq || []).map((q) => q.questionId));
            setBanco((todas || []).filter((q) => !yaUsadas.has(q.questionId)));
        } catch (error) {
            console.error(error);
        }
        setLoadingPreguntas(false);
    };

    useEffect(() => { cargarPreguntas(); }, [exam.examId]);

    const guardarConfig = async () => {
        const user = utils.getUserInformation();
        try {
            await classroomExamService.updateExam({ ...formData, modificationUser: user?.userId });
            Swal.fire('Listo', 'Examen actualizado.', 'success');
            setEditando(false);
            onSaved();
        } catch (error) {
            Swal.fire('Ha ocurrido un error', 'Por favor intentelo más tarde', 'error');
        }
    };

    const agregarPregunta = async () => {
        if (!preguntaSeleccionada) return;
        const user = utils.getUserInformation();
        const esPersonalizado = exam.gradingModeId === types.classroomExamGradingMode.WEIGHTED;
        if (esPersonalizado && !pesoNuevo) {
            Swal.fire('Campo obligatorio', 'Indica el porcentaje de esta pregunta.', 'error');
            return;
        }
        try {
            await classroomExamService.addExamQuestion({
                examId: exam.examId,
                questionId: Number(preguntaSeleccionada),
                weightPercentage: esPersonalizado ? Number(pesoNuevo) : undefined,
                createdUser: user?.userId,
                modificationUser: user?.userId
            });
            setPreguntaSeleccionada('');
            setPesoNuevo('');
            cargarPreguntas();
        } catch (error) {
            Swal.fire('Ha ocurrido un error', error?.response?.data?.message || 'Por favor intentelo más tarde', 'error');
        }
    };

    const quitarPregunta = (examQuestionId) => {
        Swal.fire({ title: '¿Quitar esta pregunta del examen?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Sí, quitar' })
            .then(async (result) => {
                if (result.isConfirmed) {
                    await classroomExamService.removeExamQuestion(examQuestionId);
                    cargarPreguntas();
                }
            });
    };

    const actualizarPeso = async (examQuestionId, weightPercentage) => {
        try {
            await classroomExamService.updateExamQuestionWeight(examQuestionId, Number(weightPercentage), utils.getUserInformation()?.userId);
            cargarPreguntas();
        } catch (error) {
            Swal.fire('Ha ocurrido un error', error?.response?.data?.message || 'Por favor intentelo más tarde', 'error');
        }
    };

    return (
        <>
            <div className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <strong>{exam.title}</strong>
                    <button className="btn btn-sm btn-outline-info" onClick={() => setEditando(!editando)}>
                        <i className="fas fa-pencil-alt me-1"></i> {editando ? 'Cancelar' : 'Editar'}
                    </button>
                </div>
                <div className="card-body">
                    {!editando ? (
                        <div className="row text-muted small">
                            <div className="col-auto">Duración: {exam.durationMinutes ? `${exam.durationMinutes} min` : 'Sin límite'}</div>
                            <div className="col-auto">Intentos permitidos: {exam.attemptsAllowed}</div>
                            <div className="col-auto">Nota máxima: {exam.maxScore}</div>
                            <div className="col-auto">Modo: {exam.gradingModeName}</div>
                            <div className="col-auto">{exam.shuffleQuestions ? 'Preguntas mezcladas' : 'Orden fijo'}</div>
                            <div className="col-auto">{exam.showResultsToStudent ? 'Muestra resultados al estudiante' : 'No muestra resultados'}</div>
                        </div>
                    ) : (
                        <div>
                            <div className="mb-3">
                                <label className="form-label">Instrucciones</label>
                                <HtmlEditor value={formData.instructionsHtml || ''} onChange={(v) => setFormData({ ...formData, instructionsHtml: v })} />
                            </div>
                            <div className="row">
                                <div className="col-md-3 mb-3">
                                    <label className="form-label">Duración (min)</label>
                                    <input type="number" className="form-control" value={formData.durationMinutes || ''} onChange={(e) => setFormData({ ...formData, durationMinutes: e.target.value || null })} />
                                </div>
                                <div className="col-md-3 mb-3">
                                    <label className="form-label">Intentos permitidos</label>
                                    <input type="number" className="form-control" value={formData.attemptsAllowed} onChange={(e) => setFormData({ ...formData, attemptsAllowed: e.target.value })} />
                                </div>
                                <div className="col-md-3 mb-3">
                                    <label className="form-label">Nota máxima</label>
                                    <input type="number" step="0.01" className="form-control" value={formData.maxScore} onChange={(e) => setFormData({ ...formData, maxScore: e.target.value })} />
                                </div>
                                <div className="col-md-3 mb-3">
                                    <label className="form-label">Ponderación</label>
                                    <select className="form-control form-select" value={formData.gradingModeId} onChange={(e) => setFormData({ ...formData, gradingModeId: Number(e.target.value) })}>
                                        <option value={types.classroomExamGradingMode.EQUAL}>Automática (igual)</option>
                                        <option value={types.classroomExamGradingMode.WEIGHTED}>Personalizada (%)</option>
                                    </select>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">Disponible desde</label>
                                    <input type="datetime-local" className="form-control" value={formData.availableFrom ? formData.availableFrom.substring(0, 16) : ''} onChange={(e) => setFormData({ ...formData, availableFrom: e.target.value })} />
                                </div>
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">Disponible hasta</label>
                                    <input type="datetime-local" className="form-control" value={formData.availableTo ? formData.availableTo.substring(0, 16) : ''} onChange={(e) => setFormData({ ...formData, availableTo: e.target.value })} />
                                </div>
                                <div className="col-md-4 mb-3 d-flex align-items-end gap-3">
                                    <div className="form-check">
                                        <input type="checkbox" className="form-check-input" id="shuffle" checked={formData.shuffleQuestions} onChange={(e) => setFormData({ ...formData, shuffleQuestions: e.target.checked })} />
                                        <label className="form-check-label" htmlFor="shuffle">Mezclar</label>
                                    </div>
                                    <div className="form-check">
                                        <input type="checkbox" className="form-check-input" id="showResults" checked={formData.showResultsToStudent} onChange={(e) => setFormData({ ...formData, showResultsToStudent: e.target.checked })} />
                                        <label className="form-check-label" htmlFor="showResults">Mostrar resultados</label>
                                    </div>
                                </div>
                            </div>
                            <button className="btn btn-primary" onClick={guardarConfig}>Guardar cambios</button>
                        </div>
                    )}
                </div>
            </div>

            <div className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <strong>Preguntas del examen</strong>
                    {weightStatus && (
                        <span className={`badge ${weightStatus.isValid ? 'bg-success' : 'bg-warning text-dark'}`}>
                            Peso total: {weightStatus.totalWeight}% {weightStatus.isValid ? '(listo)' : '(debe sumar 100%)'}
                        </span>
                    )}
                </div>
                <div className="card-body">
                    {loadingPreguntas ? <Loading /> : (
                        <>
                            {examQuestions.length === 0 ? (
                                <p className="text-muted">Todavía no hay preguntas vinculadas.</p>
                            ) : (
                                <ul className="list-group mb-3">
                                    {examQuestions.map((eq) => (
                                        <li className="list-group-item d-flex justify-content-between align-items-center" key={eq.examQuestionId}>
                                            <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(eq.statementHtml) }} />
                                            <span className="d-flex align-items-center gap-2">
                                                {exam.gradingModeId === types.classroomExamGradingMode.WEIGHTED ? (
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        className="form-control form-control-sm"
                                                        style={{ width: '90px' }}
                                                        defaultValue={eq.weightPercentage}
                                                        onBlur={(e) => actualizarPeso(eq.examQuestionId, e.target.value)}
                                                    />
                                                ) : (
                                                    <span className="badge bg-light text-dark">{eq.weightPercentage}%</span>
                                                )}
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => quitarPregunta(eq.examQuestionId)}><i className="fas fa-times"></i></button>
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <div className="input-group">
                                <select className="form-control form-select" value={preguntaSeleccionada} onChange={(e) => setPreguntaSeleccionada(e.target.value)}>
                                    <option value="">Selecciona una pregunta del banco...</option>
                                    {banco.map((q) => (
                                        <option key={q.questionId} value={q.questionId}>{q.statementHtml.replace(/<[^>]+>/g, '').slice(0, 80)}</option>
                                    ))}
                                </select>
                                {exam.gradingModeId === types.classroomExamGradingMode.WEIGHTED && (
                                    <input type="number" step="0.01" className="form-control" style={{ maxWidth: '110px' }} placeholder="%" value={pesoNuevo} onChange={(e) => setPesoNuevo(e.target.value)} />
                                )}
                                <button className="btn btn-primary" onClick={agregarPregunta}>Agregar</button>
                            </div>
                            <Link to={`/aula/${exam.classroomId}/preguntas`} className="d-inline-block mt-2">
                                <i className="fas fa-plus me-1"></i> Crear una pregunta nueva en el banco
                            </Link>
                        </>
                    )}
                </div>
            </div>

            <ExamRoster exam={exam} examQuestions={examQuestions} />
        </>
    );
};

const ExamRoster = ({ exam, examQuestions }) => {
    const [roster, setRoster] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viendo, setViendo] = useState(null);

    const load = async () => {
        try {
            const data = await classroomExamService.getRoster(exam.examId);
            setRoster(data || []);
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    };

    useEffect(() => { load(); }, [exam.examId]);

    if (loading) return <Loading />;

    return (
        <div className="card">
            <div className="card-header"><strong>Estudiantes ({roster.length})</strong></div>
            <div className="table-responsive">
                <table className="table mb-0">
                    <thead><tr><th>Estudiante</th><th>Intento</th><th>Estado</th><th>Nota</th><th></th></tr></thead>
                    <tbody>
                        {roster.map((r) => (
                            <tr key={r.studentId}>
                                <td>{r.studentName}</td>
                                <td>{r.attemptNumber || '-'}</td>
                                <td>{r.statusName ? <span className="badge bg-secondary">{r.statusName}</span> : <span className="badge bg-light text-dark">Sin intentar</span>}</td>
                                <td>{r.score ?? '-'} / {exam.maxScore}</td>
                                <td>{r.attemptId && <button className="btn btn-sm btn-outline-primary" onClick={() => setViendo(r)}>Ver / Calificar</button>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {viendo && <AttemptGradeModal exam={exam} rosterItem={viendo} examQuestions={examQuestions} onClose={() => { setViendo(null); load(); }} />}
        </div>
    );
};

const AttemptGradeModal = ({ exam, rosterItem, examQuestions, onClose }) => {
    const [attempt, setAttempt] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        const a = await classroomExamService.getAttempt(rosterItem.attemptId);
        setAttempt(a);
        setLoading(false);
    };
    useEffect(() => { load(); }, [rosterItem.attemptId]);

    const eqById = Object.fromEntries(examQuestions.map((eq) => [eq.examQuestionId, eq]));

    const calificar = async (answerId, points, feedbackHtml) => {
        await classroomExamService.gradeAnswer(answerId, Number(points), feedbackHtml, utils.getUserInformation()?.userId);
        load();
    };

    return (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{rosterItem.studentName} — Intento {rosterItem.attemptNumber}</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {loading ? <Loading /> : (
                            <>
                                <p><strong>Puntaje: {attempt.score ?? '-'} / {exam.maxScore}</strong></p>
                                {attempt.answers.map((a) => {
                                    const eq = eqById[a.examQuestionId];
                                    if (!eq) return null;
                                    const esObjetiva = OBJECTIVE_TYPES.includes(eq.questionTypeId);
                                    return (
                                        <div className="border rounded p-2 mb-2" key={a.answerId}>
                                            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(eq.statementHtml) }} />
                                            {esObjetiva ? (
                                                <div className="mt-2">
                                                    <span className={`badge ${a.isCorrect ? 'bg-success' : 'bg-danger'}`}>{a.isCorrect ? 'Correcta' : 'Incorrecta'}</span>
                                                    <span className="ms-2 text-muted">Puntos: {a.pointsAwarded ?? 0}</span>
                                                </div>
                                            ) : (
                                                <GradeAnswerInline answer={a} onGrade={calificar} />
                                            )}
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-outline-secondary" onClick={onClose}>Cerrar</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const GradeAnswerInline = ({ answer, onGrade }) => {
    const [points, setPoints] = useState(answer.pointsAwarded ?? '');
    const [feedback, setFeedback] = useState(answer.feedbackHtml || '');
    return (
        <div className="mt-2">
            {answer.answerText && <p className="mb-1"><em>{answer.answerText}</em></p>}
            {answer.document?.url && <p><a href={answer.document.url} target="_blank" rel="noreferrer">Ver archivo entregado</a></p>}
            <div className="d-flex gap-2 align-items-center">
                <input type="number" step="0.01" className="form-control form-control-sm" style={{ width: '90px' }} value={points} onChange={(e) => setPoints(e.target.value)} placeholder="Puntos" />
                <input type="text" className="form-control form-control-sm" value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Retroalimentación" />
                <button className="btn btn-sm btn-primary" onClick={() => onGrade(answer.answerId, points, feedback)}>Calificar</button>
            </div>
        </div>
    );
};

// =====================================================================
// Vista estudiante: presentar el examen cronometrado
// =====================================================================
const ExamStudentView = ({ exam }) => {
    const [studentId, setStudentId] = useState(null);
    const [attempts, setAttempts] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const estudiante = await estudiantesService.getEstudiantesPorUsuario();
            if (!estudiante || !estudiante.studentId) { setLoading(false); return; }
            setStudentId(estudiante.studentId);
            const data = await classroomExamService.getAttemptsForStudent(exam.examId, estudiante.studentId);
            setAttempts(data || []);
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    };

    useEffect(() => { load(); }, [exam.examId]);

    if (loading) return <Loading />;
    if (!studentId) return <div className="alert alert-warning">No encontramos tu información de estudiante.</div>;

    const enCurso = attempts.find((a) => a.statusId === types.classroomAttemptStatus.EN_CURSO);

    const iniciar = async () => {
        try {
            const nuevo = await classroomExamService.startAttempt(exam.examId, studentId, utils.getUserInformation()?.userId);
            setAttempts([...attempts, nuevo]);
        } catch (error) {
            Swal.fire('No se pudo iniciar', error?.response?.data?.message || 'Por favor intentelo más tarde', 'error');
        }
    };

    if (enCurso) {
        return <ExamTake exam={exam} attempt={enCurso} onFinish={load} />;
    }

    return (
        <div className="card">
            <div className="card-header"><strong>{exam.title}</strong></div>
            <div className="card-body">
                {exam.instructionsHtml && <div className="mb-3" dangerouslySetInnerHTML={{ __html: sanitizeHtml(exam.instructionsHtml) }} />}
                <p className="text-muted">Duración: {exam.durationMinutes ? `${exam.durationMinutes} minutos` : 'Sin límite'} · Intentos permitidos: {exam.attemptsAllowed}</p>

                {attempts.length > 0 && (
                    <ul className="list-group mb-3">
                        {attempts.map((a) => (
                            <li className="list-group-item d-flex justify-content-between" key={a.attemptId}>
                                <span>Intento {a.attemptNumber}</span>
                                <span>
                                    <span className="badge bg-secondary me-2">{a.statusName}</span>
                                    {exam.showResultsToStudent && a.score !== null && `${a.score} / ${exam.maxScore}`}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}

                {attempts.length < exam.attemptsAllowed ? (
                    <button className="btn btn-primary" onClick={iniciar}>
                        {attempts.length > 0 ? 'Nuevo intento' : 'Iniciar examen'}
                    </button>
                ) : (
                    <div className="alert alert-info mb-0">Ya usaste todos tus intentos permitidos.</div>
                )}
            </div>
        </div>
    );
};

const ExamTake = ({ exam, attempt, onFinish }) => {
    const [preguntas, setPreguntas] = useState([]);
    const [respuestas, setRespuestas] = useState({});
    const [loading, setLoading] = useState(true);
    const [segundosRestantes, setSegundosRestantes] = useState(null);
    const enviandoRef = useRef(false);

    useEffect(() => {
        const cargar = async () => {
            const eq = await classroomExamService.getExamQuestions(exam.examId, true);
            setPreguntas(eq || []);
            setLoading(false);
        };
        cargar();
    }, [exam.examId]);

    useEffect(() => {
        if (!attempt.expiresDate) return;
        const tick = () => {
            const restante = Math.floor((new Date(attempt.expiresDate).getTime() - Date.now()) / 1000);
            setSegundosRestantes(restante);
            if (restante <= 0) {
                enviar();
            }
        };
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attempt.expiresDate]);

    const setRespuesta = (examQuestionId, data) => {
        setRespuestas((prev) => ({ ...prev, [examQuestionId]: { ...prev[examQuestionId], ...data } }));
    };

    const subirArchivoRespuesta = async (examQuestionId, file) => {
        const doc = await documentService.addDocument(types.documentTypes.EXAM_ANSWER_FILE, file, attempt.attemptId, file.name, file.size, file.type);
        setRespuesta(examQuestionId, { documentId: doc.documentId });
    };

    const enviar = async () => {
        if (enviandoRef.current) return;
        enviandoRef.current = true;
        const answers = preguntas.map((eq) => ({ examQuestionId: eq.examQuestionId, ...(respuestas[eq.examQuestionId] || {}) }));
        try {
            await classroomExamService.submitAttempt(attempt.attemptId, answers, utils.getUserInformation()?.userId);
            Swal.fire('Examen entregado', '', 'success');
            onFinish();
        } catch (error) {
            Swal.fire('Ha ocurrido un error', 'Por favor intentelo más tarde', 'error');
            enviandoRef.current = false;
        }
    };

    if (loading) return <Loading />;

    const minutos = segundosRestantes !== null ? Math.max(0, Math.floor(segundosRestantes / 60)) : null;
    const segundos = segundosRestantes !== null ? Math.max(0, segundosRestantes % 60) : null;

    return (
        <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
                <strong>{exam.title}</strong>
                {segundosRestantes !== null && (
                    <span className={`badge ${segundosRestantes < 60 ? 'bg-danger' : 'bg-warning text-dark'}`}>
                        <i className="fas fa-clock me-1"></i> {minutos}:{String(segundos).padStart(2, '0')}
                    </span>
                )}
            </div>
            <div className="card-body">
                {preguntas.map((eq, index) => (
                    <div className="border rounded p-3 mb-3" key={eq.examQuestionId}>
                        <p><strong>{index + 1}.</strong> <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(eq.statementHtml) }} /></p>

                        {(eq.questionTypeId === types.classroomQuestionTypes.VERDADERO_FALSO || eq.questionTypeId === types.classroomQuestionTypes.OPCION_UNICA) &&
                            eq.options?.map((opt) => (
                                <div className="form-check" key={opt.optionId}>
                                    <input
                                        type="radio"
                                        className="form-check-input"
                                        name={`q-${eq.examQuestionId}`}
                                        checked={respuestas[eq.examQuestionId]?.selectedOptionId === opt.optionId}
                                        onChange={() => setRespuesta(eq.examQuestionId, { selectedOptionId: opt.optionId })}
                                    />
                                    <label className="form-check-label">{opt.optionText}</label>
                                </div>
                            ))}

                        {eq.questionTypeId === types.classroomQuestionTypes.OPCION_MULTIPLE &&
                            eq.options?.map((opt) => {
                                const seleccionadas = respuestas[eq.examQuestionId]?.selectedOptionIds || [];
                                return (
                                    <div className="form-check" key={opt.optionId}>
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={seleccionadas.includes(opt.optionId)}
                                            onChange={(e) => {
                                                const nuevas = e.target.checked ? [...seleccionadas, opt.optionId] : seleccionadas.filter((id) => id !== opt.optionId);
                                                setRespuesta(eq.examQuestionId, { selectedOptionIds: nuevas });
                                            }}
                                        />
                                        <label className="form-check-label">{opt.optionText}</label>
                                    </div>
                                );
                            })}

                        {eq.questionTypeId === types.classroomQuestionTypes.PREGUNTA_ABIERTA && (
                            <textarea
                                className="form-control"
                                rows="3"
                                value={respuestas[eq.examQuestionId]?.answerText || ''}
                                onChange={(e) => setRespuesta(eq.examQuestionId, { answerText: e.target.value })}
                            />
                        )}

                        {eq.questionTypeId === types.classroomQuestionTypes.SUBIR_ARCHIVO && (
                            <input type="file" className="form-control" onChange={(e) => e.target.files[0] && subirArchivoRespuesta(eq.examQuestionId, e.target.files[0])} />
                        )}
                    </div>
                ))}
                <button className="btn btn-primary" onClick={enviar}>Entregar examen</button>
            </div>
        </div>
    );
};

export default ExamManage;
